import { DateObject } from "react-multi-date-picker";
import RHFCheckbox from "../../Component/Forms/RHFCheckbox";
import RHFDatePicker from "../../Component/Forms/RHFDatePicker";
import RHFFileInput from "../../Component/Forms/RHFFileInput";
import RHFMultiSelectSearch from "../../Component/Forms/RHFMultiSelect";
import RHFRadioGroup from "../../Component/Forms/RHFRadioGroup";
import RHFSelectField from "../../Component/Forms/RHFSelectField";
import RHFTextArea from "../../Component/Forms/RHFTextArea";
import RHFTextField from "../../Component/Forms/RHFTextField";
import RHFToggleSwitch from "../../Component/Forms/RHFToggleSwitch";
import RHFSearchableSelect from "../../Component/Forms/RHFSearchableSelect";

export type BaseFieldConfig = {
  name: string;
  label: string;
  type:
    | "text"
    | "number"
    | "select"
    | "searchable-select"
    | "date"
    | "file"
    | "checkbox"
    | "radio"
    | "textarea"
    | "multiselect"
    | "time"
    | "datetime-local"
    | "toggle"
    | "email"
    | "password";
  col?: number;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  options?: { value: string; label: string; disabled?: boolean }[];
  dynamicOptions?: string;
  when?: (values: any) => boolean;
  accept?: string;
  rows?: number;
  multiple?: boolean;
  valueKey?: string;
  labelKey?: string;
  toggleLabels?: [string, string];
  disableSundays?: boolean;
  onlySundays?: boolean;
  disabledDates?: string[]; // ["YYYY-MM-DD", ...]
  range?: boolean;
  withPasswordToggle?: boolean;
  minDate?: string | Date | DateObject; // optional passthroughs if needed
  maxDate?: string | Date | DateObject;
  allowedDates?: string[];
  resolveFileUrl?: (value: string) => string;
  exclusiveWith?: string;
  className?: string;
  inputClassName?: string;
  optionClassName?: string;
  labelClassName?:string;
};
export const DynamicFieldRenderer = (
  field: BaseFieldConfig,
  options: { value: string; label: string; disabled?: boolean }[] = []
) => {
  switch (field.type) {
    case "text":
    case "number":
    case "time":
    case "datetime-local":
    case "email":
    case "password":
      return (
        <RHFTextField
          key={field.name}
          name={field.name}
          label={field.label}
          type={field.type}
          disabled={field.disabled}
          placeholder={field.placeholder}
          required={field.required}
          withPasswordToggle={field.withPasswordToggle ?? true} // default on
          className={field.className}
          labelClassName={field.labelClassName}
        />
      );

    case "textarea":
      return (
        <RHFTextArea
          key={field.name}
          name={field.name}
          label={field.label}
          rows={field.rows ?? 5}
          disabled={field.disabled}
          className={field.className}
          required={field.required}
        />
      );

    case "select":
      return (
        <RHFSelectField
          key={field.name}
          name={field.name}
          label={field.label}
          options={options}
          className={field.className}
          inputClassName={field.inputClassName}
          optionClassName={field.optionClassName}
          placeholderOption={field.placeholder}
          required={field.required}
        />
      );

    case "searchable-select":
      return (
        <RHFSearchableSelect
          key={field.name}
          name={field.name}
          label={field.label}
          options={options}
          placeholder={field.placeholder}
          disabled={field.disabled}
          className={field.className}
        />
      );

    case "date":
      return (
        <RHFDatePicker
          key={field.name}
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          disabled={field.disabled}
          allowedDates={field.allowedDates}
          disableSundays={field.disableSundays}
          onlySundays={field.onlySundays}
          disabledDates={field.disabledDates}
          multiple={field.multiple ?? false}
          range={field.range ?? false}
          minDate={field.minDate}
          maxDate={field.maxDate}
          required={field.required}
          labelClassName={field.labelClassName}
        />
      );

    case "file":
      return (
        <RHFFileInput
          key={field.name}
          name={field.name}
          label={field.label}
          accept={field.accept}
          multiple={field.multiple}
          required={field.required}
          resolveFileUrl={field.resolveFileUrl}
        />
      );

    case "checkbox":
      return (
        <RHFCheckbox
          key={field.name}
          name={field.name}
          label={field.label}
          disabled={field.disabled}
          exclusiveWith={field.exclusiveWith}
        />
      );

    case "radio":
      return (
        <RHFRadioGroup
          key={field.name}
          name={field.name}
          label={field.label}
          options={options}
          inline
        />
      );

    case "multiselect":
      const normalizedOptions =
        options?.map((opt) => ({
          id: opt.value,
          name: opt.label,
        })) ?? [];

      return (
        <RHFMultiSelectSearch
          key={field.name}
          name={field.name as any}
          label={field.label}
          placeholder={field.placeholder ?? "Select Options"}
          options={normalizedOptions}
          multiple={field.multiple ?? true}
          disabled={field.disabled}
          labelKey={field.labelKey ?? "name"}
        />
      );

    case "toggle":
      return (
        <RHFToggleSwitch
          key={field.name}
          name={field.name}
          label={field.label}
          disabled={field.disabled}
          toggleLabels={field.toggleLabels ?? ["Yes", "No"]}
        />
      );

    default:
      return null;
  }
};
